import { useState, useEffect, useMemo } from 'react';
import { EditFormData, TradeCount, TradeEntry } from '../types/jobsite-details.types';
import { 
    lookupService, 
    Sector, 
    Industry, 
    FundingRes, 
    Province, 
    Trade as BaseTrade, 
    JobCompStatus 
} from '../services/lookups.service';
import { Contractor, ContractorService } from '../services/contractor.service';
import { toast } from 'react-toastify';

// Extend the base Trade interface to include category
interface Trade extends BaseTrade {
    category?: string;
}

export const useEditForm = () => {
    const [editFormData, setEditFormData] = useState<EditFormData>({
        jobSiteName: '',
        subsite: '',
        contact: '',
        projectType: '',
        percentageCompleted: '',
        isUnion: false,
        contractor: '',
        address: '',
        city: '',
        country: '',
        state: '',
        postalCode: '',
        workerLabel: [],
        workerCount: '',
        superintendent: '',
        siteContactEmail: '',
        sectors: [],
        industry: '',
        fundingResource: '',
        activityStatus: 'active',
        completionStatus: 'start',
        completionStep: 'start',
        address1: '',
        address2: '',
        workersType: 'union',
        tradesCount: '',
        tradeType: '',
        files: [],
        markAsComp: false,
        hasPLA: false,
        hasMRF: false
    });

    // Lookup data states
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [fundingResources, setFundingResources] = useState<FundingRes[]>([]);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [trades, setTrades] = useState<Trade[]>([]);
    const [jobCompletionStatuses, setJobCompletionStatuses] = useState<JobCompStatus[]>([]);
    const [contractors, setContractors] = useState<Contractor[]>([]);

    // Form state
    const [currentStep, setCurrentStep] = useState(1);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingText, setProcessingText] = useState('');

    // Trade-related states
    const [tradeCounts, setTradeCounts] = useState<TradeCount[]>([]);
    const [tradeEntries, setTradeEntries] = useState<Record<string, TradeEntry[]>>({});
    const [unionTypes, setUnionTypes] = useState<Record<string, string>>({});
    const [isSignificant, setIsSignificant] = useState<Record<string, string>>({});
    const [selectedLocals, setSelectedLocals] = useState<Record<string, string>>({});

    // Contractor form states
    const [showAddContractorDialog, setShowAddContractorDialog] = useState(false);
    const [contractorFormData, setContractorFormData] = useState({
        name: '',
        email: '',
        contractorSectors: [],
    });
    const [contractorFormErrors, setContractorFormErrors] = useState<{
        name?: string;
        email?: string;
        contractorSectors?: string;
        ownerName?: string;
    }>({});

    // Fetch lookup data
    useEffect(() => {
        const fetchLookupData = async () => {
            try {
                const [
                    sectorData,
                    industriesData,
                    fundingData,
                    provincesData,
                    tradesData,
                    statusData,
                    contractorsData
                ] = await Promise.all([
                    lookupService.getSectors(),
                    lookupService.getIndustries(),
                    lookupService.getFundingRes(),
                    lookupService.getProvinces(),
                    lookupService.getTrades(),
                    lookupService.getJobCompStatus(),
                    ContractorService.getContractor()
                ]);

                setSectors(sectorData);
                setIndustries(industriesData);
                setFundingResources(fundingData);
                setProvinces(provincesData);
                setTrades(tradesData);
                setJobCompletionStatuses(statusData);
                setContractors(contractorsData);
            } catch (error) {
                console.error('Error fetching lookup data:', error);
            }
        };

        fetchLookupData();
    }, []);

    // Completion steps memo
    const completionSteps = useMemo(() => {
        const apiSteps = jobCompletionStatuses.map(status => ({
            label: status.status,
            value: status.id.toString()
        }));
        return [...apiSteps];
    }, [jobCompletionStatuses]);

    // Update completion step to first available when completion statuses are loaded
    useEffect(() => {
        if (jobCompletionStatuses.length > 0 && editFormData.completionStep === 'start') {
            setEditFormData(prev => ({
                ...prev,
                completionStep: jobCompletionStatuses[0].id.toString(),
                completionStatus: jobCompletionStatuses[0].status
            }));
        }
    }, [jobCompletionStatuses, editFormData.completionStep]);

    // Validation functions
    const validateField = (name: keyof EditFormData, value: string | boolean | string[] | File[] | undefined) => {
        if (value === undefined) {
            return '';
        }
        if (Array.isArray(value)) {
            return value.length === 0 ? `${name} is required` : '';
        }

        switch (name) {
            case 'jobSiteName':
                return value.toString().trim() === '' ? 'Jobsite name is required' : '';
            case 'superintendent':
                return value.toString().trim() === '' ? 'Site Contact/Superintendent is required' : '';
            case 'siteContactEmail':
                if (value.toString().trim() === '') {
                    return 'Site Contact Email is required';
                }
                const emailRegexSecure = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
                return !emailRegexSecure.test(value.toString()) ? 'Please enter a valid email address' : '';
            case 'sectors':
                return Array.isArray(value) && value.length === 0 ? 'At least one sector is required' : '';
            case 'industry':
                // Only require industry if exactly ONE sector is selected and it has industries
                const hasIndustries = editFormData.sectors && editFormData.sectors.length === 1 &&
                    industries.some(industry => editFormData.sectors.includes(industry.sector.id.toString()));
                return hasIndustries && value.toString().trim() === '' ? 'Industry is required' : '';
            case 'address1':
                return value.toString().trim() === '' ? 'Address Line 1 is required' : '';
            case 'city':
                return value.toString().trim() === '' ? 'City is required' : '';
            case 'postalCode':
                if (value.toString().trim() === '') {
                    return 'Postal Code is required';
                }
                const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
                return !postalCodeRegex.test(value.toString().trim()) ? 'Please enter a valid postal code (e.g., A1A 1A1)' : '';
            case 'state':
                return value.toString().trim() === '' ? 'Province is required' : '';
            default:
                return '';
        }
    };

    const validateStep1 = () => {
        const newErrors: Record<string, string> = {};
        let hasErrors = false;

        const requiredFields: Array<keyof EditFormData> = [
            'jobSiteName',
            'superintendent',
            'siteContactEmail',
            'sectors',
            'industry'
        ];

        requiredFields.forEach(field => {
            const error = validateField(field, editFormData[field]);
            if (error) {
                newErrors[field] = error;
                hasErrors = true;
            }
        });

        if (!editFormData.contractor) {
            newErrors.contractor = 'Please select a contractor';
            hasErrors = true;
        }

        setErrors(newErrors);
        return !hasErrors;
    };

    const validateStep2 = () => {
        const newErrors: Record<string, string> = {};
        let hasErrors = false;

        const requiredFields: Array<keyof EditFormData> = [
            'address1',
            'city',
            'postalCode',
            'state'
        ];

        requiredFields.forEach(field => {
            const error = validateField(field, editFormData[field]);
            if (error) {
                newErrors[field] = error;
                hasErrors = true;
            }
        });

        setErrors(newErrors);
        return !hasErrors;
    };

    const validateStep3 = () => {
        const newErrors: Record<string, string> = {};
        let hasErrors = false;

        if (!editFormData.workerLabel || editFormData.workerLabel.length === 0) {
            newErrors.workerLabel = 'Please select at least one label';
            hasErrors = true;
        }

        if (!editFormData.workersType) {
            newErrors.workersType = 'Please select the type of workers';
            hasErrors = true;
        }

        setErrors(newErrors);
        return !hasErrors;
    };

    const validateStep5 = () => {
        const newErrors: Record<string, string> = {};
        let hasErrors = false;

        if (!editFormData.tradesCount) {
            newErrors.tradesCount = 'Please select the number of trades seen on site';
            hasErrors = true;
        }

        if (!editFormData.tradeType) {
            newErrors.tradeType = 'Please select at least one trade type';
            hasErrors = true;
        }

        if (tradeCounts.length === 0) {
            newErrors.tradeType = 'Please add trade details';
            hasErrors = true;
        }

        tradeCounts.forEach((trade) => {
            const isUA = trades.find(t => t.id === trade.tradeId)?.category === 'UA';
            const unionStatus = unionTypes[trade.tradeId];
            const isSignificantTrade = isSignificant[trade.tradeId] === 'yes';

            if (!unionStatus) {
                newErrors[`trade_${trade.tradeId}_union_status`] = `Please select union status for ${trade.tradeName}`;
                hasErrors = true;
            }

            if (!isUA) {
                if (!trade.workers || trade.workers === '0') {
                    newErrors[`trade_${trade.tradeId}_workers`] = `Please enter number of workers for ${trade.tradeName}`;
                    hasErrors = true;
                }
            }

            if (isUA && isSignificantTrade && !selectedLocals[trade.tradeId]) {
                newErrors[`trade_${trade.tradeId}_local_union`] = `Please select local union for ${trade.tradeName}`;
                hasErrors = true;
            }

            const entries = tradeEntries[trade.tradeId] || [];
            if (entries.length === 0) {
                newErrors[`trade_${trade.tradeId}_entries`] = `Please add at least one entry for ${trade.tradeName}`;
                hasErrors = true;
            }

            entries.forEach((entry, index) => {
                if (!entry.contractorId) {
                    newErrors[`trade_${trade.tradeId}_entry_${index}_contractor`] = `Please select a contractor for entry ${index + 1}`;
                    hasErrors = true;
                }

                if ((entry.journeymen || 0) === 0 && (entry.apprentices || 0) === 0) {
                    newErrors[`trade_${trade.tradeId}_entry_${index}_workers`] = `At least one of Journeymen or Apprentices must be greater than 0 for entry ${index + 1}`;
                    hasErrors = true;
                }
            });
        });

        setErrors(newErrors);
        return !hasErrors;
    };

    // Contractor form handlers
    const handleContractorInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    // const handleContractorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setContractorFormErrors(prev => ({ ...prev, [name]: undefined }));

        if (name === 'name') {
            if (/\d/.test(value)) {
                setContractorFormErrors(prev => ({
                    ...prev,
                    name: 'Contractor name cannot contain numbers'
                }));
                return;
            }

            if (value.length > 50) {
                setContractorFormErrors(prev => ({
                    ...prev,
                    name: 'Contractor name must be 50 characters or less'
                }));
                return;
            }
        } else if (name === 'email') {
            if (value.includes(' ')) {
                setContractorFormErrors(prev => ({
                    ...prev,
                    email: 'Email cannot contain spaces'
                }));
                return;
            }

            const emailRegexSecure = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

            if (value && !emailRegexSecure.test(value)) {
                setContractorFormErrors(prev => ({
                    ...prev,
                    email: 'Please enter a valid email address'
                }));
            }
        } else if (name === 'ownerName') {
            if (/\d/.test(value)) {
                setContractorFormErrors(prev => ({
                    ...prev,
                    ownerName: 'Owner name cannot contain numbers'
                }));
                return;
            }

            if (value.length > 50) {
                setContractorFormErrors(prev => ({
                    ...prev,
                    ownerName: 'Owner name must be 50 characters or less'
                }));
                return;
            }
        }

        setContractorFormData({
            ...contractorFormData,
            [name]: value
        });
    };

    const handleAddContractor = async () => {
        const trimmedEmail = contractorFormData.email.trim();

        if (!contractorFormData.name || !trimmedEmail || !contractorFormData.contractorSectors.length) {
            toast.error('Please fill in all required fields');
            return;
        }

        // Validation checks
        if (/\d/.test(contractorFormData.name)) {
            setContractorFormErrors(prev => ({ ...prev, name: 'Contractor name cannot contain numbers' }));
            toast.error('Contractor name cannot contain numbers');
            return;
        }


        if (trimmedEmail.includes(' ')) {
            setContractorFormErrors(prev => ({ ...prev, email: 'Email cannot contain spaces' }));
            toast.error('Email cannot contain spaces');
            return;
        }

        const emailRegexSecure = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!emailRegexSecure.test(trimmedEmail)) {
            setContractorFormErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
            toast.error('Please enter a valid email address');
            return;
        }

        if (contractorFormData.name.length > 50) {
            setContractorFormErrors(prev => ({ ...prev, name: 'Contractor name must be 50 characters or less' }));
            toast.error('Contractor name must be 50 characters or less');
            return;
        }


        if (contractorFormErrors.name || contractorFormErrors.email || contractorFormErrors.contractorSectors) {
            toast.error('Please fix the errors before submitting');
            return;
        }

        try {
            const contractorData: any = {
                name: contractorFormData.name,
                email: trimmedEmail,
                contractorSectors: contractorFormData.contractorSectors,
            };

            await ContractorService.createContractor(contractorData);

            const updatedContractors = await ContractorService.getContractor();
            setContractors(updatedContractors);

            setContractorFormData({
                name: '',
                email: '',
                contractorSectors: [],
            });

            setShowAddContractorDialog(false);
            toast.success('Contractor added successfully');
        } catch (error) {
            console.error('Error adding contractor:', error);
            toast.error((error as Error).message || 'An unknown error occurred');
        }
    };

    return {
        editFormData,
        setEditFormData,
        sectors,
        industries,
        fundingResources,
        provinces,
        trades,
        contractors,
        setContractors,
        completionSteps,
        currentStep,
        setCurrentStep,
        errors,
        setErrors,
        isProcessing,
        setIsProcessing,
        processingText,
        setProcessingText,
        tradeCounts,
        setTradeCounts,
        tradeEntries,
        setTradeEntries,
        unionTypes,
        setUnionTypes,
        isSignificant,
        setIsSignificant,
        selectedLocals,
        setSelectedLocals,
        showAddContractorDialog,
        setShowAddContractorDialog,
        contractorFormData,
        setContractorFormData,
        contractorFormErrors,
        setContractorFormErrors,
        validateStep1,
        validateStep2,
        validateStep3,
        validateStep5,
        handleContractorInputChange,
        handleAddContractor
    };
}; 